import { useNavigate } from 'react-router';
import { refreshBeastModeExpiration } from '@gonfalon/beastmode/refreshBeastModeExpiration';
import {
  beastModeProfilePageRevamp,
  enableCheckYourEmailEndpoint,
  enableIntercomChatAI,
  isMFARedirectEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { fromJS, isImmutable } from '@gonfalon/immutable-shim/immutableShim';
import { resetIntercomInstance } from '@gonfalon/intercom-fin-app/resetIntercomInstance';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import {
  toAccountSettings,
  toCheckYourEmail,
  toChooseOrganization,
  toChooseOrganizationCheckYourEmail,
  toHref,
  toUnauthenticatedVerifyEmail,
  toVerifyOrganizationEmail,
} from '@gonfalon/navigator/navigators';

import {
  navigateAfterSignup,
  navigateToDashboard,
  redirectToDashboardOrContinue,
  redirectToProfile,
  redirectToSSOProvider,
} from '#actions/navigation';
import { useDispatch } from '#hooks/useDispatch';
import type { GetState, GlobalDispatch } from '#reducers/state';
// eslint-disable-next-line import/no-namespace
import * as AuthAPI from '#sources/AuthAPI';
import {
  resendChooseOrganizationEmailVerification as resendChooseOrganizationEmailVerificationAPI,
  resendUnverifiedMemberEmailVerification as unverifiedMemberAPIResendEmailVerification,
} from '#sources/UnverifiedMemberAPI';
import type { LoginInfo, SignInExperienceType } from '#utils/loginUtils';
import { redirectToSSO } from '#utils/loginUtils';
import Logger from '#utils/logUtils';
import { setOAuthCredentials } from '#utils/oauthCredentialsStorage';
import type { GenerateActionType } from '#utils/reduxUtils';
import type { ForgotPassword, ResetPassword } from '#utils/resetPasswordUtils';
import { isAgentControlSignupPostAuthPending } from '#utils/signupPostAuthDestinationStorage';
import { getIsValidRedirectPath, mergeQueryParamsIntoPath } from '#utils/urlUtils';

import { activateBeastMode } from './beastMode';
import { setLoginEndTimeMetricIfValid, setLoginExperienceType } from './timeToLoginMetric';

import { useSendChooseOrganizationVerificationEmailMutation } from '#queries/authQueries';

const logger = Logger.get('AuthActions');

const loginRequiresMfa = (token: string) =>
  ({
    type: 'auth/LOGIN_WITH_MFA',
    token,
  }) as const;

type MfaEnrollmentResponse = { mfaEnrollmentRequired?: boolean; token?: string };

export type MfaEnrollmentState = { token?: string; redirect?: string };

// Parses a 200 login response body as MfaEnrollmentResponse, returning null when the
// response is malformed. The server's Content-Type says JSON but content isn't guaranteed —
// a truncated response or upstream misconfiguration can throw inside res.json() and reject
// the login promise with a non-ImmutableServerError, which then crashes the error handler.
async function parseMfaEnrollmentResponse(res: Response): Promise<MfaEnrollmentResponse | null> {
  try {
    return (await res.json()) as MfaEnrollmentResponse;
  } catch (err) {
    logger.error('Failed to parse login response body as JSON', err);
    return null;
  }
}

// Routes an MFA-enforcement login response to the enrollment interstitial, passing the
// enrollment JWT via history state (not the URL) so the TOTP secret stays out of logs.
async function navigateToMfaEnrollment(
  navigate: ReturnType<typeof useNavigate>,
  body: MfaEnrollmentResponse,
  redirectPath: string | undefined,
  isValidRedirectPath: boolean,
): Promise<boolean> {
  if (!body.mfaEnrollmentRequired) {
    return false;
  }
  // An enrollment 200 must carry a token; without one, the interstitial would render an
  // expired-session card and the user has no path forward. Signal missing-token to the
  // caller so it can dispatch loginFailed instead.
  if (!body.token) {
    return false;
  }
  const state: MfaEnrollmentState = {
    token: body.token,
    redirect: redirectPath && isValidRedirectPath ? redirectPath : undefined,
  };
  await navigate('/enroll-mfa', { state });
  return true;
}

const clearMFA = () =>
  ({
    type: 'auth/CLEAR_MFA_STATE',
  }) as const;

const loginRequiresPassword = (error?: ImmutableServerError) =>
  ({
    type: 'auth/LOGIN_WITH_PASSWORD',
    error,
  }) as const;

const loginFailed = (error: ImmutableServerError) =>
  ({
    type: 'auth/LOGIN_FAILED',
    error,
  }) as const;

const loginDone = (authenticate: boolean) =>
  ({
    type: 'auth/LOGIN_DONE',
    authenticate,
  }) as const;

const loginAction = (info: LoginInfo) =>
  ({
    type: 'auth/LOGIN',
    info,
  }) as const;

function useRedux() {
  const dispatch = useDispatch();
  const setBeastMode = () => dispatch(activateBeastMode());
  const setTimeToLoginType = (type: SignInExperienceType) => dispatch(setLoginExperienceType(type));
  const setTimeToLoginEndTimeMetric = (time: number) => dispatch(setLoginEndTimeMetricIfValid(time));
  const dispatchLoginRequiresMfa = (token: string) => dispatch(loginRequiresMfa(token));
  const navigate = useNavigate();
  return {
    dispatch,
    dispatchLoginRequiresMfa,
    setBeastMode,
    setTimeToLoginType,
    setTimeToLoginEndTimeMetric,
    navigate,
  };
}

/** Clears the MFA state so that the UI does not get stuck & prompt you to use MFA when switching between accounts. */
export const clearMFAState = () => (dispatch: GlobalDispatch) => {
  dispatch(clearMFA());
};

/**
 * Takes the login info, and determines whether the user needs to enter a password or login with SSO. Redirects
 * to user to their SSO provider or the appropriate login page.
 */
export const useFindLoginPage = () => {
  const { dispatch, dispatchLoginRequiresMfa, setTimeToLoginType, setTimeToLoginEndTimeMetric, navigate } = useRedux();
  const sendEmailVerification = useSendChooseOrganizationVerificationEmailMutation();

  return (info: LoginInfo, redirectPath?: string) => {
    dispatch(loginAction(info));
    const email = info.get('username');

    return AuthAPI.getLoginPage(email, redirectPath)
      .then(async (res) => {
        // special case for Domain Matching redirects
        if (
          res?.redirected &&
          res?.url &&
          (res.url.includes('join-your-organization') || res.url.includes('welcome'))
        ) {
          window.location.assign(res.url);
          return;
        }

        if (res?.redirected && res?.url?.includes('/choose-organization/')) {
          // 303 - Special case for multi-account SSO logins. Redirect to the SSO provider.
          // Pass through the redirect parameter so it's preserved after org selection
          const searchParams = new URLSearchParams();
          if (redirectPath) {
            searchParams.set('redirect', redirectPath);
          }
          await navigate(toChooseOrganization({ email }, { search: searchParams.toString() }));
          return;
        } else if (res.status === 200) {
          // Password account member, email associated w/ 1 account.
          dispatch(loginRequiresPassword());
          // it is at this point we call loginV2, but until the backend is stood up, we can't access this
        } else if (res.status === 204) {
          // SSO Member, email associated w/ 1 account.
          const destination = redirectPath || '';
          setTimeToLoginType('SSO');
          setTimeToLoginEndTimeMetric(Date.now());

          return redirectToSSO({
            res,
            dispatch,
            redirectPath: destination,
            redirectToSSOProvider,
            dispatchLoginRequiresMfa,
          });
        }
      })
      .catch((error) => {
        const status = error.get('status');

        // The user cannot access the list of organizations until they verify their email.
        if (status === 403) {
          // Send the email, and then direct the user to the Verify Email page.
          sendEmailVerification
            .mutateAsync(email)
            .then(async () => {
              if (enableCheckYourEmailEndpoint()) {
                await navigate(toChooseOrganizationCheckYourEmail(), { state: { email } });
              } else {
                await navigate(toVerifyOrganizationEmail({ email }));
              }
            })
            .catch((e) => dispatch(loginFailed(e)));
          return; // Redirect & return early to prevent dispatching the loginFailed action which would flash an error message on the screen.
        }

        dispatch(loginFailed(error));
      });
  };
};

export const useBeastMode = () => {
  const { dispatch, navigate, setBeastMode } = useRedux();

  return (info: LoginInfo, redirectPath: string) =>
    AuthAPI.escalateSession(info).then(
      async () => {
        setBeastMode();
        refreshBeastModeExpiration();

        if (!beastModeProfilePageRevamp()) {
          await navigate(redirectPath || toAccountSettings());
        }

        dispatch(loginDone(true));
      },
      (error) => {
        const status = error.get('status');
        if (status === 401) {
          dispatch(loginRequiresPassword(error));
        } else {
          dispatch(loginFailed(error));
        }
      },
    );
};

export const useLogin = () => {
  const { dispatch, setBeastMode, setTimeToLoginType, setTimeToLoginEndTimeMetric, navigate } = useRedux();

  return (info: LoginInfo, redirectPath: string, authenticate: boolean) => {
    dispatch(loginAction(info));

    const loginInfo = authenticate ? AuthAPI.escalateSession : AuthAPI.login;
    const isValidRedirectPath = getIsValidRedirectPath(redirectPath);

    return loginInfo(info).then(
      async (res) => {
        // special case for Domain Matching redirects
        if (res?.redirected && res?.url && res.url.includes('join-your-org')) {
          window.location.assign(res.url);
          return;
        }
        // A non-MFA login here returns 200 with no body; only parse the JSON enforcement payload.
        if (res.status === 200 && !res?.redirected && res.headers?.get('Content-Type')?.includes('application/json')) {
          const body = await parseMfaEnrollmentResponse(res);
          if (body === null) {
            dispatch(loginFailed(fromJS({ message: 'Login failed. Try again.' }) as ImmutableServerError));
            return;
          }
          if (await navigateToMfaEnrollment(navigate, body, redirectPath, isValidRedirectPath)) {
            return;
          }
          // MFA enforcement requested but no token — the interstitial can't render usefully.
          if (body.mfaEnrollmentRequired) {
            logger.error('MFA enrollment response missing token');
            dispatch(
              loginFailed(fromJS({ status: 200, message: 'Something went wrong. Try again.' }) as ImmutableServerError),
            );
            return;
          }
        }
        // MFA or SSO
        if (res.status === 202) {
          setTimeToLoginType('SSO');
          setTimeToLoginEndTimeMetric(Date.now());

          const location = res.headers && res.headers.get('Location');
          if (location) {
            const destination: string = redirectPath || (authenticate ? toHref(toAccountSettings()) : '');
            // SSO
            redirectToSSOProvider(location, destination);
          } else {
            return res.json().then(
              // MFA
              (r: { token: string }) => dispatch(loginRequiresMfa(r.token)),
            );
          }
          return;
        } else if (authenticate) {
          setBeastMode();
          refreshBeastModeExpiration();

          if (!beastModeProfilePageRevamp()) {
            let redirect = redirectPath;

            if (!redirect) {
              const settings = toAccountSettings();
              redirect = `${settings.pathname}${settings.search}${settings.hash}`;
            }

            window.location.assign(redirect);
          }
        } else if (redirectPath && isValidRedirectPath) {
          window.location.href = redirectPath;
        } else if (isAgentControlSignupPostAuthPending()) {
          navigateAfterSignup(info.get('username'));
        } else {
          redirectToDashboardOrContinue();
        }

        dispatch(loginDone(authenticate));
      },
      async (error) => {
        const status = error.get('status');
        const message = error.get('message');

        // 400 - Indicates that you need to choose an organization because it's a multi-organization user.
        if (status === 400 && message === 'You must choose which account to log into') {
          const email = info.get('username');
          // Persist OAuth credentials across the choose-organization redirect (BIS-2536)
          const oauth = info.get('oauth');
          if (oauth) {
            setOAuthCredentials({ provider: oauth.provider, token: oauth.token });
          }
          // Pass through the redirect parameter so it's preserved after org selection
          const searchParams = new URLSearchParams();
          if (redirectPath) {
            searchParams.set('redirect', redirectPath);
          }
          await navigate(toChooseOrganization({ email }, { search: searchParams.toString() }));
        } else if (status === 401) {
          dispatch(loginRequiresPassword(error));
        } else {
          dispatch(loginFailed(error));
        }
      },
    );
  };
};

export const useLoginV2 = () => {
  const location = window.location;
  const { dispatch, dispatchLoginRequiresMfa, navigate, setTimeToLoginType, setTimeToLoginEndTimeMetric } = useRedux();
  const sendEmailVerification = useSendChooseOrganizationVerificationEmailMutation();

  return (info: LoginInfo, redirectPath?: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('redirect'); // Don't include the redirect param itself in the destination
    searchParams.delete('code'); // Don't forward OAuth callback params to the redirect destination
    searchParams.delete('state');

    // Use proper URL merging that handles existing query params
    const pathWithParams = redirectPath ? mergeQueryParamsIntoPath(redirectPath, searchParams) : undefined;
    const isValidRedirectPath = pathWithParams ? getIsValidRedirectPath(pathWithParams) : false;
    const email = info.get('username');

    return AuthAPI.loginV2(info, isValidRedirectPath ? pathWithParams : undefined)
      .then(async (res) => {
        if (res.status === 201) {
          if (pathWithParams && isValidRedirectPath) {
            window.location.assign(pathWithParams);
          } else {
            navigateToDashboard();
          }
          if (enableIntercomChatAI()) {
            resetIntercomInstance();
          }
        } else if (res?.redirected && (res.url.includes('join-your-organization') || res.url.includes('welcome'))) {
          window.location.assign(res.url);
        } else if (res?.redirected && res?.url?.includes('/choose-organization/')) {
          // Persist OAuth credentials across the choose-organization redirect (BIS-2536)
          const oauth = info.get('oauth');
          if (oauth) {
            setOAuthCredentials({ provider: oauth.provider, token: oauth.token });
          }
          // For OAuth logins, email (from info.get('username')) is empty — extract from
          // the server's redirect URL which contains the correct email from the OAuth payload.
          const redirectEmail =
            email || decodeURIComponent(new URL(res.url).pathname.split('/choose-organization/')[1] || '');
          // Pass through the redirect parameter so it's preserved after org selection
          const orgSearchParams = new URLSearchParams();
          if (redirectPath) {
            orgSearchParams.set('redirect', redirectPath);
          }
          await navigate(toChooseOrganization({ email: redirectEmail }, { search: orgSearchParams.toString() }));
          return;
        } else if (res?.redirected && res?.url) {
          // For single SSO accounts, redirect to the SSO provider.
          window.location.assign(res.url);
        } else if (res.status === 200 && !res?.redirected) {
          const body = await parseMfaEnrollmentResponse(res);
          if (body === null) {
            dispatch(loginFailed(fromJS({ message: 'Login failed. Try again.' }) as ImmutableServerError));
            return;
          }
          // MFA enforcement routes to enrollment; otherwise fall through to the code input.
          if (!(await navigateToMfaEnrollment(navigate, body, pathWithParams, isValidRedirectPath))) {
            if (body.token) {
              dispatch(loginRequiresMfa(body.token));
            } else {
              // A non-enrollment 200 must carry an MFA token; surface a failure instead of
              // rendering a verification prompt that can never succeed with an empty token.
              logger.error('MFA login response missing token');
              dispatch(
                loginFailed(
                  fromJS({ status: 200, message: 'Something went wrong. Try again.' }) as ImmutableServerError,
                ),
              );
            }
          }
        } else if (res.status === 204) {
          setTimeToLoginType('SSO');
          setTimeToLoginEndTimeMetric(Date.now());

          return redirectToSSO({
            res,
            dispatch,
            redirectPath: pathWithParams || '',
            redirectToSSOProvider,
            dispatchLoginRequiresMfa,
          });
        }
      })
      .catch(async (error) => {
        if (!isImmutable(error)) {
          logger.error('auth error', error);
          dispatch(loginFailed(error));
          return;
        }

        const status = error.get('status');

        // The user cannot access the list of organizations until they verify their email for multi-account members.
        if (status === 403) {
          // Send the email, and then direct the user to the Verify Email page.
          sendEmailVerification
            .mutateAsync(email)
            .then(async () => {
              if (enableCheckYourEmailEndpoint()) {
                await navigate(toChooseOrganizationCheckYourEmail(), { state: { email } });
              } else {
                await navigate(toVerifyOrganizationEmail({ email }));
              }
            })
            .catch((e) => dispatch(loginFailed(e)));
          return; // Redirect & return early to prevent dispatching the loginFailed action which would flash an error message on the screen.
        } else if (status === 422) {
          if (enableCheckYourEmailEndpoint()) {
            await navigate(toCheckYourEmail(), { state: { email } });
          } else {
            await navigate(toUnauthenticatedVerifyEmail({ email }));
          }

          return; // Redirect & return early to prevent dispatching the loginFailed action which would flash an error message on the screen.
        }
        dispatch(loginFailed(error as ImmutableServerError));
      });
  };
};

const resetPasswordFailed = (error: ImmutableServerError) =>
  ({
    type: 'auth/RESET_PASSWORD_FAILED',
    error,
  }) as const;

const resetPasswordAction = (reset: ResetPassword) =>
  ({
    type: 'auth/RESET_PASSWORD',
    reset,
  }) as const;

export const resetPassword = (reset: ResetPassword) => (dispatch: GlobalDispatch) => {
  dispatch(resetPasswordAction(reset));
  return AuthAPI.resetPassword(reset).then(
    (res) => {
      // no MFA
      if (res.status === 204) {
        redirectToDashboardOrContinue();
      } else {
        return res.json().then(
          // MFA
          (response: { token: string }) => dispatch(loginRequiresMfa(response.token)),
        );
      }
    },
    (error) => dispatch(resetPasswordFailed(error)),
  );
};

const verifyMFAFailed = (error: ImmutableServerError) =>
  ({
    type: 'auth/VERIFY_MFA_FAILED',
    error,
  }) as const;

const recoverMFAFailed = (error: ImmutableServerError) =>
  ({
    type: 'auth/RECOVER_MFA_FAILED',
    error,
  }) as const;

const verifyMFAAction = () =>
  ({
    type: 'auth/VERIFY_MFA',
  }) as const;

export const verifyMFA =
  (token: string, code: string, redirectPath?: string, accountID?: string) => (dispatch: GlobalDispatch) => {
    dispatch(verifyMFAAction());
    return AuthAPI.loginWithMFA(token, code, accountID).then(
      (res) => {
        const isValidRedirectPath = redirectPath ? getIsValidRedirectPath(redirectPath) : false;
        if (redirectPath && isValidRedirectPath && isMFARedirectEnabled()) {
          window.location.assign(redirectPath);
        } else if (res?.redirected && res?.url) {
          window.location.assign(res.url);
        } else {
          redirectToDashboardOrContinue();
        }
      },
      (error) => {
        dispatch(verifyMFAFailed(error));
      },
    );
  };

const recoverMFAAction = () =>
  ({
    type: 'auth/RECOVER_MFA',
  }) as const;

export const recoverMFA = (token: string, code: string, accountID?: string) => (dispatch: GlobalDispatch) => {
  dispatch(recoverMFAAction());
  return AuthAPI.loginWithMFARecovery(token, code, accountID).then(
    () => {
      redirectToProfile('mfa-reset');
    },
    (error) => {
      dispatch(recoverMFAFailed(error));
    },
  );
};

export const createUseMFARecoveryAction = () =>
  ({
    type: 'auth/USE_MFA_RECOVERY',
  }) as const;

const forgotPasswordAction = (details: ForgotPassword) =>
  ({
    type: 'auth/FORGOT_PASSWORD',
    details,
  }) as const;

const forgotPasswordDone = (details: ForgotPassword) =>
  ({
    type: 'auth/FORGOT_PASSWORD_DONE',
    details,
  }) as const;

const forgotPasswordFailed = (details: ForgotPassword, error: ImmutableServerError) =>
  ({
    type: 'auth/FORGOT_PASSWORD_FAILED',
    details,
    error,
  }) as const;

export const forgotPassword = (details: ForgotPassword) => (dispatch: GlobalDispatch) => {
  dispatch(forgotPasswordAction(details));
  return AuthAPI.forgotPassword(details)
    .then((res) => {
      switch (res.status) {
        case 204:
        default:
          dispatch(forgotPasswordDone(details));
      }
    })
    .catch((error) => dispatch(forgotPasswordFailed(details, error)));
};

const editForgotPasswordAction = (field: 'username', details: ForgotPassword) =>
  ({
    type: 'auth/EDIT_FORGOT_PASSWORD',
    details,
    field,
  }) as const;

export const editForgotPassword =
  (field: 'username', value: string) => (dispatch: GlobalDispatch, getState: GetState) => {
    const formState = getState().forgotPasswordForm;
    return dispatch(editForgotPasswordAction(field, formState.modified.set(field, value)));
  };

const resendEmailVerificationStart = () =>
  ({
    type: 'auth/RESEND_EMAIL_VERIFICATION_START',
  }) as const;

const resendEmailVerificationDone = (email: string, response: void | Response) =>
  ({
    type: 'auth/RESEND_EMAIL_VERIFICATION_DONE',
    email,
    response,
  }) as const;

export const resendEmailVerificationBlocked = () =>
  ({
    type: 'auth/RESEND_EMAIL_VERIFICATION_BLOCKED',
  }) as const;

export const resendEmailVerificationUnblocked = () =>
  ({
    type: 'auth/RESEND_EMAIL_VERIFICATION_UNBLOCKED',
  }) as const;

const resendEmailVerificationFailed = (error: ImmutableServerError) =>
  ({
    type: 'auth/RESEND_EMAIL_VERIFICATION_FAILED',
    error,
  }) as const;

export function resendUnverifiedMemberEmailVerification(email: string, customReferer?: string) {
  return (dispatch: GlobalDispatch) => {
    dispatch(resendEmailVerificationStart());
    return unverifiedMemberAPIResendEmailVerification(email, customReferer)
      .then((res) => dispatch(resendEmailVerificationDone(email, res)))
      .catch((error: ImmutableServerError) => dispatch(resendEmailVerificationFailed(error)));
  };
}

export function resendChooseOrganizationEmailVerification(email: string) {
  return (dispatch: GlobalDispatch) => {
    dispatch(resendEmailVerificationStart());
    return resendChooseOrganizationEmailVerificationAPI(email)
      .then((res) => dispatch(resendEmailVerificationDone(email, res)))
      .catch((error: ImmutableServerError) => dispatch(resendEmailVerificationFailed(error)));
  };
}

const AuthActionCreators = {
  clearMFA,
  loginRequiresMfa,
  loginRequiresPassword,
  loginFailed,
  loginDone,
  resetPasswordAction,
  resetPasswordFailed,
  verifyMFAAction,
  recoverMFAAction,
  verifyMFAFailed,
  recoverMFAFailed,
  createUseMFARecoveryAction,
  forgotPasswordAction,
  forgotPasswordFailed,
  forgotPasswordDone,
  editForgotPasswordAction,
  loginAction,
  resendEmailVerificationStart,
  resendEmailVerificationDone,
  resendEmailVerificationBlocked,
  resendEmailVerificationUnblocked,
  resendEmailVerificationFailed,
};

export type AuthAction = GenerateActionType<typeof AuthActionCreators>;
